define((require) => {
    "use strict";

    var {contains, isString, extend}    = require("underscore"),
        Class                           = require("shared/util/Class"),
        GUID                            = require("shared/util/GUID"),
        LoggedInUserState               = require("shared/ui/user/LoggedInUserState"),
        {Logger}                        = require("shared/util/Logger"),
        Permissions                     = require("shared/security/Permissions"),
        Preconditions                   = require("shared/util/Preconditions"),
        Promise                         = require("shared/util/Promise"),
        {isBlank}                       = require("shared/util/StringUtils"),
        Syncer                          = require("shared/services/sync/Syncer"),
        SyncManager                     = require("shared/services/sync/SyncManager"),
        UrlUtil                         = require("shared/util/UrlUtil");

    var DEFAULT_SITE_ID = "SiteGenesis",
        SHOP_PREFIX = "/s/" + DEFAULT_SITE_ID + "/dw/shop/",
        log = new Logger("SalesforceCommerce");

    /**
     * @class {SalesforceCommerce}
     * A <i>connect</i> that will handle all connections for SalesforceCommerce. <p>
     * <ul>
     *     <li> Possibility to connect to the <tt>SalesforceCommerce Open Commerce Api</tt>.
     *     <li> Test connections given urls or clients.
     *     <li> Try to infer the  SalesforceCommerce web address.
     *     <li> Validate the settings of a conn\ection like clientId or Url.
     * </ul>
     * @author Pedro Augusto/ <a href="http://www.zmags.com">Zmags</a>
     */
    var SalesforceCommerce = Class.extend("SalesforceCommerce", {

        /**
         * {SalesforceCommerceSettingsModel} SalesforceCommerce settings model
         */
        getSettings: function() {
            /** @see {WidgetTypes.SALESFORCE_COMMERCE} */
            return LoggedInUserState.get("companySettings").demandware();
        },

        /**
         * Returns the SalesforceCommerce site id based on the internal site Id (the Mongo ObjectId of the site).
         * @param {String} siteObjectId the site Id to search for.
         * @returns {String} the siteId if it can be found or undefined if not or the company does not have any sites.
         */
        getSiteByObjectId: function(siteObjectId) {
            if (!siteObjectId) {
                return null;
            }
            var sites = this.getSettings().get("sites");

            if (sites) {
                var site = sites.get(siteObjectId);
                return site ? site.get("siteId") : null;
            }
            return null;
        },

        /**
         * Versions of the SalesforceCommerceApi where the widget was tested.
         */
        supportedVersions: ["v15_1", "v15_2", "v15_4", "v14_8", "v14_6", "v14_2"],

        /**
         * Generate possibilities according to the url provided by the user.
         * Those possibilities are the SalesforceCommerce versions tested by this API.
         *
         * TODO - remove these possibilities check if we expose versioning on the integrations page.
         *
         * @param {String} url The url for the SalesforceCommerce service endpoint.
         * @param {String} siteId The id of the SalesforceCommerce site it's trying to connect to.
         * @return {String[]} The array of url possibilities; never falsey, never empty.
         */
        generatePossibilities: function(url, siteId) {
            Preconditions.checkArgument(url);

            var self = this,
                shopPrefix = SHOP_PREFIX,
                possibilities = [],
                // If there aren't http or https, it will include https.
                newUrl = UrlUtil.prependHTTP(url, true),
                // Given an URL it will generate possibilities for all supported versions.
                generateUrlsByDifferentBaseAddress = function(url) {
                    self.supportedVersions.forEach((version) => {
                        possibilities.push(UrlUtil.concat(url, shopPrefix, version) + "/");
                    });
                };

            if (siteId) {
                shopPrefix = SHOP_PREFIX.replace(DEFAULT_SITE_ID, siteId);
            }

            // OK, I will generate combinations for the first set of address.
            generateUrlsByDifferentBaseAddress(newUrl);

            // Well, but if base url was modified it means that the userUrl didn't contains a https OR http.
            if (newUrl !== url) {
                // This is not the perfect scenario, but let' try to connect using HTTP.
                newUrl = UrlUtil.prependHTTP(url);

                // Generate again all possibilities for http.
                generateUrlsByDifferentBaseAddress(newUrl);
            }

            return possibilities;
        },

        /**
         * Extract the version give a SalesforceCommerce url.
         * @param {String} url
         */
        extractVersion: function(url) {
            Preconditions.checkArgument(url);

            var regex = new RegExp("/dw/shop/(.*)+/"),
                matches = regex.exec(url);

            return matches ? matches[1] : matches;
        },

        /**
         * Returns true if the supplied client id is a valid SalesforceCommerce client id (UUID), false otherwise.
         *
         * @return {Boolean} True if valid, false otherwise.
         */
        validateClientId: function(clientId) {
            return isString(clientId) && GUID.validate(clientId);
        },

        /**
         * Validate if url is null or a supported version.
         * @param {String} url address provided by the user.
         * @return {String} Returns true if the supplied url has a supported version, false otherwise.
         */
        validateUrl: function(url) {
            var error = null,
                version =  this.extractVersion(url);

            if (!url || isBlank(url)) {
                error = "You must provide a valid Salesforce Commerce Cloud address";
            } else if (version) {
                if (!contains(this.supportedVersions, version)) {
                    error = `The version ${version} is not supported`;
                    log.info(`Cannot use unsupported version of Salesforce Commerce Cloud: ${version}`);
                }
            }

            return error;
        },

        /**
         * Validate basic parameters of the server settings. It won't try to connect
         * for the SalesforceCommerce Server, however, it will validate at least if there are
         * parameters passed.
         *
         * @param {SalesforceCommerceSettingsModel} settings to be validated.
         * @return {Boolean} True if valid, false otherwise.
         */
        validateServerSettings: function(settings) {
            Preconditions.checkArgument(settings);

            log.info("Validating for Client ID", settings.get("clientId"), " userUrl ", settings.get("userUrl"));

            return this.validateClientId(settings.get("clientId")) && !this.validateUrl(settings.get("userUrl"));
        },

        /**
         * The system will try to connect for the address provided for the url,
         * but it will also try to connect for some address possibilities.
         * For example, if the user inputs: <p>
         *   input:   salesforcecommerce.zmags.com <p>
         *   output:<p>
         * <ol>
         * <li>http://salesforcecommerce.zmags.com/  (invalid, but user friendly)
         * <li>http://salesforcecommerce.zmags.com/dw/shop/v15_1/ (valid)
         * <li> The system will take this as a priority. The reason why this version of the WebApi is a priority is
         * because was the one used for develop this app. However, the system was also tested with other api versions.
         * <li> http://salesforcecommerce.zmags.com/dw/shop/v15_2/ (valid)
         * <li>  http://salesforcecommerce.zmags.com/dw/shop/v15_4/ (valid)
         * </ol>
         * @param {String} url provided for the user.
         * @param {String} clientId SalesforceCommerce clientId.
         * @return {Promise} A promise that will resolve with the resolved url.
         */
        inferAddress: function(url, clientId, siteId) {
            var self = this,
                possibilities = this.generatePossibilities(url, siteId),
                deferred = Promise.deferred();

            log.info("Infer address for: " + possibilities);

            var tryConnect = function(possibility) {
                log.info("Testing: <" + possibility + ">...");

                self.testConnect(possibility, clientId).done(() => {
                    log.info("Success, could connect to: <" + possibility + ">");

                    deferred.resolve(possibility);
                }).fail(() => {
                    log.info("Fail, could not connect to: <" + possibility + ">");

                    if (possibilities.length === 0) {
                        deferred.reject();
                    } else {
                        tryConnect(possibilities.shift());
                    }
                });
            };

            if (possibilities.length > 0) {
                tryConnect(possibilities.shift());
            }

            return deferred.promise();
        },

        /**
         * Wrapper function to call the restApi.
         *
         * @param {Object} options (method,url(sufix of the webapi))
         * @return {Promise} A promise that will resolve once the api call completes/fails.
         */
        callRestApi: function(options) {
            var settings = this.getSettings(),
                baseURL = settings.get("webApiUrl"),
                data = extend({
                    // eslint-disable-next-line camelcase
                    client_id: settings.get("clientId"),
                    expand: "images,prices,availability"
                }, options.data);

            if (options.siteId) {
                baseURL = baseURL.replace(DEFAULT_SITE_ID, options.siteId);
            }

            return this._call({
                method: options.method,
                fn: {
                    url: baseURL + options.url,
                    type: "GET",
                    data: data,
                    dataType: "json"
                },
                privilege: Permissions.Privileges.CREATE,
                resultHandler: options.resultHandler,
                errorHandler: function() {
                    return false;
                }
            }, options.args);
        },

        /**
         * Test connections with a SalesforceCommerce server.
         *
         * @param {String} url with the SalesforceCommerce server address
         * @param {String} clientId allowed to crossdomain communication.
         * @return {Promise} returns a Promise.
         */
        testConnect: function(url, clientId) {
            return this._call({
                method: "connect",
                options: {
                    // The default timeout of SyncManager is 20seconds, but 3 seconds
                    // must to be enough to validate a connection.
                    timeout: 3000
                },
                fn: {
                    url:      UrlUtil.concat(url, "product_search"),
                    type:     "GET",
                    // eslint-disable-next-line camelcase
                    data:     {q: "", client_id: clientId},
                    dataType: "json"
                },
                errorHandler: function() {
                    return false;
                }
            });
        },

        /**
         * @private
         * Invoke the SyncManager creating an queue for each request.
         *
         * @param  {Object} request The request to execute using its own queue; never falsey.
         * @param  {Object} [id]    The id to queue on, e.g. a product id or a search term. Can be falsey.
         * @return {Promise} A promise that will resolve once the request (queue) is empty.
         */
        _call: function(request, id) {
            // Create a target to queue on, primed with supplied id, if any. This means that we will queue based on
            // the id only, e.g. a product id, so two different products can be fetched concurrently.
            var target = {
                id:        id,
                toString:  function() {
                    var s = "SalesforceCommerce[method: " + request.method;
                    if (id) {
                        s += "; arg: '" + id + "'";
                    }
                    return s + "]";
                }
            };

            Syncer.State.init(target, {unique: ["id"]});

            // Get queue for this connector.
            var queue = SyncManager.queue(target);

            // Execute the specific request and get a promise for when it is done.
            var promise = queue.enqueue(request);

            // Start sync, if not already
            queue.sync();

            // Return the promise for "request" (not for the entire queue)
            return promise;
        },

        isWidgetEnabled: function() {
            return this.getSettings().get("enabled");
        },

        isQuickViewEnabled: function() {
            return this.getSettings().get("enabled");
        }
    });

    return new SalesforceCommerce();
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/salesforceCommerceWidget/service/SalesforceCommerceConnect.js